import React, { forwardRef } from 'react';
import { GalleryTileWithRef } from '../../../../GalleryTile';
import { Skeleton } from '@shared/components/Skeleton';
import { useAuth } from '@shared/components/AuthProvider';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useNearViewport } from '@shared/hooks/useNearViewport';
import { desktopAspect, mobileAspect, tileRowGap, galleryDesktopAspect, galleryMobileAspect } from '../../CardsConfig';
import { CardTile } from './CardTile';
import type { CardTileProps } from './CardTile.types';

type CardTileSkeletonProps = Readonly<{
  aspectRatio: string;
  isMobile: boolean;
}>;

// Same grid, aspect ratio, and row gap as CardTile, so the row doesn't shift when the tile mounts.
// A single static box where the card preview will sit: a large gallery holds hundreds of these at
// once, and a shimmer on each costs a style recalc per box on every frame, even off screen.
const CardTileSkeleton = forwardRef<HTMLDivElement, CardTileSkeletonProps>(function CardTileSkeleton({ aspectRatio, isMobile }, ref) {
  return (
    <GalleryTileWithRef
      ref={ref}
      aspectRatio={aspectRatio}
      alignContent="start"
      rowGap={tileRowGap}
    >
      <Skeleton
        disableShimmerAnimation
        // Matches AssetPreviewFrame dimensions.
        aspectRatio={isMobile ? '8 / 10' : '9 / 10'}
      />
    </GalleryTileWithRef>
  );
});

/**
 * Defers mounting the whole tile (swatches and their tooltips, colorway resolution, favorite
 * badge, impression observer) until it is near the viewport, so a large gallery page only pays for
 * the tiles the user scrolls to.
 */
export const CardTileShell = (props: CardTileProps) => {
  const { isSuperAdmin } = useAuth();
  const isMobile = useIsMobileScreen();
  const { ref, hasBeenNear } = useNearViewport({ preloadViewports: 0.4 });

  if (!props.data.enabled && !isSuperAdmin) return null;

  // Favorites-gallery tiles show a price label and need the taller aspect.
  const showPrice = props.cardTileSource === 'favoritesGallery';
  const aspectRatio = showPrice ? (isMobile ? mobileAspect : desktopAspect) : isMobile ? galleryMobileAspect : galleryDesktopAspect;

  return hasBeenNear ? (
    <CardTile
      {...props}
      aspectRatio={aspectRatio}
    />
  ) : (
    <CardTileSkeleton
      ref={ref}
      aspectRatio={aspectRatio}
      isMobile={isMobile}
    />
  );
};
